import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PanelListSkeleton } from "@/components/devicePanel/PanelSkeleton";
import { PanelLoadError } from "@/components/devicePanel/PanelLoadError";
import { SiteWorklist } from "@/components/devicePanel/SiteWorklist";
import {
  PanelListHeader,
  STUDY_FILTERS,
  StudyTable,
  type ExtraColumn,
  type StudyFilter,
} from "@/components/devicePanel/PanelWorklist";
import { useAuth } from "@/context/AuthContext";
import { useDevicePanelStudies, useReviewProgress } from "@/hooks/useDevicePanel";
import { ReviewSwitch, taskLabel, usePanelReview } from "@/components/devicePanel/ReviewSwitch";
import { firstToReview, reviewUrl, type ReviewProgressResponse } from "@/types/devicePanelReview";
import { siteOrder } from "@/types/notificationView";
import { panelCaseUrl, type DevicePanelStudyRow } from "@/types/devicePanel";

/** Review (Review mode only) is not a row predicate: it lists the round's
 * queued studies in the backend's queue order. */
type Filter = StudyFilter | "Review";

type QueuedStudy = ReviewProgressResponse["studies"][number];

/** A queued study's progress, e.g. "1 to review · 2/3 done". */
const progressText = ({ items, reviewed_items }: QueuedStudy) =>
  `${items > reviewed_items ? `${items - reviewed_items} to review · ` : ""}${reviewed_items}/${items} done`;

/**
 * A device panel's studies in the same worklist every panel view uses, flagged
 * studies first, with a research panel's report draft count beside each. View
 * (the default) filters to all or flagged studies. A reader with a review task
 * gets a switch to Review, which adds the Review filter: the round's queue in
 * the backend's order with each study's progress, each row opening its study on
 * its first unjudged item. `?start=1` is the reader's entry link: it turns
 * Review on and goes straight to the round's first unjudged item. A panel in the
 * notification view is the site worklist instead.
 */
const DevicePanelListPage: React.FC = () => {
  const { panelId } = useParams<{ panelId: string }>();
  const [params] = useSearchParams();
  const start = params.get("start");
  const { token } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error } = useDevicePanelStudies(token, panelId);
  const progress = {
    blind: useReviewProgress(token, panelId, "blind"),
    report_check: useReviewProgress(token, panelId, "report_check"),
  };
  // The entry link is a review link in itself.
  const { task, round, setMode, unknown } = usePanelReview(
    panelId!,
    { blind: progress.blind.data, report_check: progress.report_check.data },
    start === "1",
  );
  const [chosen, setFilter] = useState<Filter>("All");
  const filter = chosen === "Review" && !round ? "All" : chosen;
  const startAt =
    start === "1" && round ? firstToReview(progress[round].data?.studies ?? []) : null;
  useEffect(() => {
    if (startAt && round)
      navigate(reviewUrl(panelId!, startAt.study_uid, round, "next"), { replace: true });
  }, [startAt, round, panelId, navigate]);
  if (data?.panel.view === "notification") return <SiteWorklist data={data} />;
  // The queues decide the review switch, so the page waits for them too.
  if (isLoading || progress.blind.isLoading || progress.report_check.isLoading || startAt)
    return <PanelListSkeleton />;
  if (error || !data) return <PanelLoadError what="device panel" error={error} />;
  if (unknown || (start !== null && start !== "1"))
    return (
      <p role="alert" className="p-6 text-sm text-fg-muted">
        This link names an unknown {unknown ?? "start"}.
      </p>
    );

  const { panel } = data;
  const findingsDevice = panel.devices.find((device) => device.role === "findings");
  const queue = (round && progress[round].data?.studies) || [];
  const caseUrl = (row: DevicePanelStudyRow) =>
    filter === "Review" && round
      ? reviewUrl(panel.id, row.study_uid, round, "next")
      : panelCaseUrl(panel.id, row.study_uid);
  const studyOf = (study: QueuedStudy) => {
    const row = data.studies.find((candidate) => candidate.study_uid === study.study_uid);
    if (!row) throw new Error(`Review queue names study ${study.label}, not in this panel`);
    return row;
  };
  const shown =
    filter === "Review" && round
      ? queue.map(studyOf)
      : siteOrder(data.studies).filter(STUDY_FILTERS[filter as StudyFilter]);
  const extra: ExtraColumn | undefined =
    filter === "Review"
      ? {
          heading: "Progress",
          cell: (row) => progressText(queue.find((study) => study.study_uid === row.study_uid)!),
        }
      : findingsDevice && {
          heading: "Report draft",
          cell: (row) =>
            row.findings_count === null
              ? "—"
              : `${row.findings_count} ${row.findings_count === 1 ? "finding" : "findings"}`,
        };
  const filters: Filter[] = [
    ...(Object.keys(STUDY_FILTERS) as StudyFilter[]),
    ...(round ? ["Review" as const] : []),
  ];
  return (
    <div className="flex h-full flex-col bg-bg text-fg">
      <PanelListHeader
        panel={panel}
        studies={data.studies}
        filters={filters}
        filter={filter}
        onFilter={setFilter}
        actions={
          task && (
            <ReviewSwitch
              label={taskLabel(task)}
              on={!!round}
              onChange={(on) => setMode(on ? "review" : "view")}
            />
          )
        }
      />
      <div className="min-h-0 flex-1 overflow-auto">
        {start === "1" && task && (
          <p role="status" className="px-4 py-3 text-[13px] text-fg-muted">
            Every item in the {task.label.toLowerCase()} is judged.
          </p>
        )}
        {start === "1" && !task && (
          <p role="status" className="px-4 py-3 text-[13px] text-fg-muted">
            You have no review task on this panel.
          </p>
        )}
        <StudyTable
          panelId={panel.id}
          rows={shown}
          devices={panel.devices}
          caseUrl={caseUrl}
          extra={extra}
        />
        {shown.length === 0 && (
          <p className="px-4 py-3 text-[13px] text-fg-dim">No {filter.toLowerCase()} studies</p>
        )}
      </div>
    </div>
  );
};

export default DevicePanelListPage;
